import React, { useEffect, useState } from "react";
import {
  Container,
  Box,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Typography,
  Grid,
  Button,
  Autocomplete,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  Divider,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import { Close, RemoveRedEyeOutlined, Search, ArrowBack } from "@mui/icons-material";
import { EditIcon } from "../comps/CustomIcons";
import { getPatientByIdApi } from "../services/patient.service";
import { getPatientApi } from "../services/patient.service";
import { useGetApi } from "../hooks/useGetApi";
import { all } from "axios";
import { getMedicinesApi } from "../services/item.service";
import { toast } from "react-toastify";
import { addPharmacyOrderApi } from "../services/medicine-tracking.service";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import Loader from "../components/Loader/loader";

const EXCLUDE_LABEL = [
  {
    id: 1,
    label: "Sl. No.",
    value: "slNo",
  },
  {
    id: 2,
    label: "Item Code",
    value: "itemCode",
  },
  {
    id: 3,
    label: "Medicine Name",
    value: "medicineName",
  },
  {
    id: 4,
    label: "Current Stock",
    value: "currentStock",
  },
  {
    id: 5,
    label: "Quantity",
    value: "quantity",
  },
  {
    id: 6,
    label: "",
    value: "",
  },
];

const PharmacyOutsider = () => {
  const { patientId } = useParams();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showPatientSearch, setShowPatientSearch] = useState(!patientId);
  const [patientSearch, setPatientSearch] = useState("");

  const initialRowData = {
    itemCode: "",
    medicineName: "",
    currentStock: "",
    quantity: "",
    _id: "",
  };

  const [excludedDepartments, setExcludedDepartments] = useState([
    { ...initialRowData },
  ]);

  // Get patient by ID (when patientId is provided)
  const shouldFetchPatient = !!(patientId && patientId !== 'undefined' && patientId !== 'null');
  
  const {
    dataList: allData,
    isLoading,
    isError,
    refetch,
  } = useGetApi({
    apiFunction: getPatientByIdApi,
    params: {
      patientId: patientId,
    },
    skip: !shouldFetchPatient, // Skip when shouldFetchPatient is false
  });

  // Get patients for search (when no patientId is provided)
  const shouldFetchPatientsList = showPatientSearch && !shouldFetchPatient;
  
  const {
    dataList: patientsList,
    isLoading: isPatientsLoading,
    isError: isPatientsError,
  } = useGetApi({
    apiFunction: getPatientApi,
    params: { 
      search: patientSearch, 
      limit: 50,
      page: 1
    },
    dependencies: [patientSearch],
    debounceDelay: 500,
    skip: !shouldFetchPatientsList, // Skip when shouldFetchPatientsList is false
  });

  const { dataList: itemList, isLoading: isItemsLoading, isError: isItemsError } = useGetApi({
    apiFunction: getMedicinesApi,
    params: { limit: Number.MAX_SAFE_INTEGER, itemType: "Medicine" },
  });

  // Debug: Log the itemList to see its structure
  console.log("itemList:", itemList);

  // Get the medicines array - handle different possible structures
  const medicinesData = itemList?.medicines || itemList?.data?.medicines || itemList || [];

  // Handler for selecting a patient from search results
  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    setShowPatientSearch(false);
  };

  // Handler for going back to patient search
  const handleBackToSearch = () => {
    setShowPatientSearch(true);
    setSelectedPatient(null);
    setExcludedDepartments([{ ...initialRowData }]);
  };

  // Get the current patient data (either from API or selected patient)
  const currentPatient = patientId ? allData : selectedPatient;

  // Function to calculate age from date of birth
  const calculateAge = (dob) => {
    if (!dob) return 'N/A';
    const birthDate = new Date(dob);
    const today = new Date();
    const age = Math.floor((today - birthDate) / (1000 * 60 * 60 * 24 * 365.25));
    return age >= 0 ? age : 'N/A';
  };

  const handleAddTableRow = () => {
    setExcludedDepartments((prevState) => [
      ...prevState,
      { ...initialRowData }, // Create a new independent row
    ]);
  };

  const handleRemoveTableRow = (rowIndex) => {
    setExcludedDepartments((prevState) =>
      prevState.filter((_, index) => index !== rowIndex)
    );
  };
  const handleAutocompleteChange = (newValue, rowIndex) => {
    if (newValue) {
      setExcludedDepartments((prevState) =>
        prevState.map((row, index) =>
          index === rowIndex
            ? {
                ...row,
                itemCode: newValue.itemCode,
                medicineName: newValue.itemName,
                currentStock: newValue.totalStock,
                _id: newValue._id,
              }
            : row
        )
      );
    }
  };
  const handleSubmit = async () => {
    if (!excludedDepartments.length) {
      toast.error("Add at least one item and try again!");
      return;
    }
    const hasInvalidQuantity = excludedDepartments.some(
      (row) => Number(row?.quantity) === 0
    );

    if (hasInvalidQuantity) {
      console.log(hasInvalidQuantity);
      toast.error("Remove the item with empty quantity and try again!");
      return;
    }
    const payload = {
      patientId: currentPatient?._id,
      UHID: currentPatient?.UHID || currentPatient?.uhid,
      medicines: excludedDepartments.map((row) => ({
        item: row?._id,
        quantity: row?.quantity,
      })),
    };
    setLoading(true);
    try {
      let response = await addPharmacyOrderApi(payload);
      if (response?.code === 200 || response?.code === 201) {
        setLoading(false);
        toast.success(
          "Pharmacy order created successfully" || response?.message
        );
        // Navigate back to pharmacy page after successful order
        setTimeout(() => {
          navigate('/pharmacy');
        }, 1500); // Wait 1.5 seconds to show the success message
      } else {
        setLoading(false);
        toast.error(response?.message || "Some error occurred.");
      }
    } catch (error) {
      setLoading(false);
      toast.error(error?.message || "Some error occurred.");
    }
  };

  const handleQuantityChange = (event, rowIndex) => {
    let { value } = event.target;
    value = Number(value);

    setExcludedDepartments((prevState) =>
      prevState.map((row, index) => {
        if (index === rowIndex) {
          if (value < 0) {
            toast.error("Quantity cannot be less than 0");
            return { ...row, quantity: 0 };
          }
          if (value > row.currentStock) {
            toast.error("Quantity cannot exceed current stock");
            return { ...row, quantity: 0 };
          }
          return { ...row, quantity: value };
        }
        return row;
      })
    );
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ width: "100%", pr: "90px", py: "20px" }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: "20px" }}>
            <IconButton 
              onClick={() => navigate('/pharmacy')} 
              sx={{ 
                mr: 2,
                backgroundColor: "#f5f5f5",
                "&:hover": {
                  backgroundColor: "#e0e0e0"
                }
              }}
            >
              <ArrowBack />
            </IconButton>
            <Typography variant="h5" sx={{ fontWeight: 600, color: "#333" }}>
              Pharmacy Outsider
            </Typography>
          </Box>

          {showPatientSearch ? (
            // Patient Search Interface
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Select Patient for Medicine Order
              </Typography>
              
              {/* Search Field */}
              <TextField
                placeholder="Search patients by name, UHID, or phone"
                size="small"
                value={patientSearch}
                onChange={(e) => setPatientSearch(e.target.value)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{ minWidth: "400px", mb: 3 }}
              />

              {/* Patients List */}
              {isPatientsLoading ? (
                <Loader />
              ) : isPatientsError ? (
                <ErrorMessage />
              ) : (
                <Grid container spacing={2}>
                  {(patientsList?.patients || patientsList)?.map((patient) => (
                    <Grid item xs={12} md={6} lg={4} key={patient._id}>
                      <Card 
                        sx={{ 
                          cursor: "pointer",
                          "&:hover": {
                            boxShadow: 3,
                            backgroundColor: "#f5f5f5"
                          }
                        }}
                        onClick={() => handlePatientSelect(patient)}
                      >
                        <CardContent>
                          <Typography variant="h6" sx={{ fontSize: "1rem", mb: 1 }}>
                            {patient.firstName} {patient.lastName}
                          </Typography>
                          <Typography color="textSecondary" variant="body2">
                            UHID: {patient.UHID || patient.uhid || 'N/A'}
                          </Typography>
                          <Typography color="textSecondary" variant="body2">
                            Phone: {patient.contactNumber || patient.phone || 'N/A'}
                          </Typography>
                          <Typography color="textSecondary" variant="body2">
                            Age: {calculateAge(patient.dob)} | Gender: {patient.gender || 'N/A'}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                  {(!(patientsList?.patients || patientsList) || (patientsList?.patients || patientsList).length === 0) && patientSearch && (
                    <Grid item xs={12}>
                      <Typography color="textSecondary" sx={{ textAlign: "center", py: 4 }}>
                        No patients found matching your search criteria
                      </Typography>
                    </Grid>
                  )}
                  {!patientSearch && (
                    <Grid item xs={12}>
                      <Typography color="textSecondary" sx={{ textAlign: "center", py: 4 }}>
                        Start typing to search for patients
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              )}
            </Box>
          ) : (
            // Medicine Order Form
            <>
              {(patientId ? isLoading : false) ? (
                <Loader />
              ) : (patientId ? isError : false) ? (
                <ErrorMessage />
              ) : (
                <>
                  {/* Patient Information */}
                  <Box
                    sx={{
                      backgroundColor: "rgba(240, 247, 252, 1)",
                      borderRadius: "12px",
                      mb: "30px",
                      p: "20px",
                    }}
                  >
                    <Typography sx={{ mb: "20px", color: "rgba(93, 106, 130, 1)" }}>
                      Patient Information
                    </Typography>
                    <Grid container spacing={2}>
                      {/* contactNumber */}
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          label="UHID"
                          value={currentPatient?.UHID || currentPatient?.uhid || "-"}
                          fullWidth
                          required
                          slotProps={{ input: { readOnly: true } }}
                        />
                      </Grid>
                      {/* uhid */}
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          label="Patient Name"
                          value={`${currentPatient?.firstName || "-"} ${
                            currentPatient?.lastName || "-"
                          }`}
                          fullWidth
                          required
                          slotProps={{ input: { readOnly: true } }}
                        />
                      </Grid>

                      {/* name */}
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          label="Contact Number"
                          value={currentPatient?.contactNo || currentPatient?.contactNumber || currentPatient?.phone || "-"}
                          fullWidth
                          required
                          slotProps={{ input: { readOnly: true } }}
                        />
                      </Grid>
                      {/* doctorName */}
                    </Grid>
                  </Box>

                  <TableContainer component={Paper} sx={{ mb: "30px" }}>
                    <Table>
                      <TableHead
                        sx={{
                          bgcolor: "#EAEAEA",
                        }}
                      >
                        <TableRow>
                          {EXCLUDE_LABEL?.map((option) => (
                            <TableCell
                              key={option?.id}
                              sx={{
                                textAlign: option?.align || "left",
                                color: "#5D6A82",
                              }}
                            >
                              {option?.label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {excludedDepartments?.map((row, rowIndex) => (
                          <TableRow key={rowIndex}>
                            <TableCell>
                              <Typography>{rowIndex + 1}</Typography>
                            </TableCell>
                            <TableCell>
                              <Autocomplete
                                options={medicinesData}
                                getOptionLabel={(option) => option?.itemCode || ""}
                                value={
                                  medicinesData.find(
                                    (item) => item?.itemCode === row?.itemCode
                                  ) || null
                                }
                                onChange={(_, newValue) =>
                                  handleAutocompleteChange(newValue, rowIndex)
                                }
                                loading={isItemsLoading}
                                noOptionsText={
                                  isItemsLoading ? "Loading medicines..." : 
                                  isItemsError ? "Error loading medicines" :
                                  "No medicines found"
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Item Code"
                                    fullWidth
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Autocomplete
                                options={medicinesData}
                                getOptionLabel={(option) => option?.itemName || ""}
                                value={
                                  medicinesData.find(
                                    (item) => item?.itemName === row?.medicineName
                                  ) || null
                                }
                                onChange={(_, newValue) =>
                                  handleAutocompleteChange(newValue, rowIndex)
                                }
                                loading={isItemsLoading}
                                noOptionsText={
                                  isItemsLoading ? "Loading medicines..." : 
                                  isItemsError ? "Error loading medicines" :
                                  "No medicines found"
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Medicine Name"
                                    fullWidth
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography>{row?.currentStock}</Typography>
                            </TableCell>
                            <TableCell>
                              <TextField
                                type="number"
                                label="Quantity"
                                name="quantity"
                                value={row?.quantity || ""}
                                onChange={(e) => handleQuantityChange(e, rowIndex)}
                                required
                                fullWidth
                                sx={{ bgcolor: "white" }}
                              />
                            </TableCell>
                            <TableCell
                              key={`${rowIndex}`}
                              sx={{ color: "#5D6A82" }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <RemoveRedEyeOutlined
                                  sx={{
                                    cursor: "pointer",
                                    fontSize: "18px",
                                  }}
                                />
                                <EditIcon
                                  sx={{
                                    cursor: "pointer",
                                    color: "transparent",
                                    fontSize: "18px",
                                  }}
                                />
                                <Close
                                  sx={{ cursor: "pointer", fontSize: "18px" }}
                                  onClick={() => handleRemoveTableRow(rowIndex)}
                                />
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell
                            colSpan={EXCLUDE_LABEL?.length}
                            sx={{ color: "#92A6CC", cursor: "pointer" }}
                            onClick={handleAddTableRow}
                          >
                            + Add item
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* submit button */}
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Button
                      variant="contained"
                      type="submit"
                      sx={{
                        minWidth: "150px",
                        textTransform: "none",
                      }}
                      onClick={handleSubmit}
                      disabled={loading}
                    >
                      {loading ? <CircularProgress size={24} /> : `Submit`}
                    </Button>
                  </Box>
                </>
              )}
            </>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default PharmacyOutsider;
